import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keyboard navigation for report tables — Tally-style, so a report can be
 * worked entirely from the keyboard.
 *
 *   ↑ / ↓          move the highlighted row (scrolls it into view)
 *   ← / →          move between columns where the caller handles it (two-column
 *                  statements), otherwise scroll the table sideways
 *   Page Up/Down   jump a screenful of rows
 *   Home / End     first / last row
 *   Enter          open the highlighted row (drill down / view)
 *   Esc, Backspace go back one level
 *
 * The listener sits on `window` but stands down whenever the user is typing in
 * a field, or a `<dialog>`-ish overlay is up — otherwise arrow keys would fight
 * the search box and the date pickers.
 */

export interface ReportKeyboardNavOptions {
  /** How many rows are currently rendered. */
  rowCount: number;
  /** Enter (or a click) on the highlighted row. */
  onActivate?: (index: number) => void;
  /** Esc / Backspace — usually "up one level" or "close". */
  onBack?: () => void;
  /**
   * ← / →. Return true if you handled it. Two-column statements use this to
   * move the cursor between the columns; anything that returns false (or omits
   * it) falls back to scrolling the container sideways.
   */
  onHorizontal?: (direction: -1 | 1) => boolean;
  /** Scroll container; also the element scrolled sideways by ← / →. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Turn the whole thing off (e.g. while a modal is open). */
  enabled?: boolean;
  /** Rows per Page Up/Down jump. */
  pageSize?: number;
  /** Pixels per horizontal step. */
  hStep?: number;
  /** Pixels per vertical step, used when there are no rows to walk. */
  vStep?: number;
}

/** True when the keystroke belongs to something the user is typing into. */
function isTypingTarget(el: EventTarget | null): boolean {
  const n = el as HTMLElement | null;
  if (!n || !n.tagName) return false;
  const tag = n.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || n.isContentEditable === true;
}

export function useReportKeyboardNav({
  rowCount,
  onActivate,
  onBack,
  onHorizontal,
  containerRef,
  enabled = true,
  pageSize = 10,
  hStep = 120,
  vStep = 60,
}: ReportKeyboardNavOptions) {
  const [activeIndex, setActiveIndex] = useState(-1);
  // Keep the latest values without re-binding the listener on every render.
  const state = useRef({ rowCount, onActivate, onBack, onHorizontal, enabled, pageSize, hStep, vStep, activeIndex });
  state.current = { rowCount, onActivate, onBack, onHorizontal, enabled, pageSize, hStep, vStep, activeIndex };

  // A shorter row set (filter changed, drilled elsewhere) must not leave the
  // highlight pointing past the end.
  useEffect(() => {
    setActiveIndex(i => (i >= rowCount ? rowCount - 1 : i));
  }, [rowCount]);

  const scrollRowIntoView = useCallback((index: number) => {
    const host = containerRef.current;
    if (!host) return;
    const row = host.querySelector<HTMLElement>(`[data-nav-index="${index}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [containerRef]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = state.current;
      if (!s.enabled || isTypingTarget(e.target)) return;

      const host = containerRef.current;

      // With no navigable rows — a statement layout rather than a row list —
      // the vertical keys fall back to plain scrolling, so every report can be
      // moved from the keyboard whatever its shape.
      const scrollBy = (dy: number) => {
        if (!host) return;
        e.preventDefault();
        host.scrollTop += dy;
      };
      const scrollTo = (top: number) => {
        if (!host) return;
        e.preventDefault();
        host.scrollTop = top;
      };
      const rowless = s.rowCount <= 0;

      const move = (next: number) => {
        e.preventDefault();
        const clamped = Math.max(0, Math.min(s.rowCount - 1, next));
        setActiveIndex(clamped);
        // Wait for the highlight to render before scrolling to it.
        requestAnimationFrame(() => scrollRowIntoView(clamped));
      };

      switch (e.key) {
        case 'ArrowDown':  return rowless ? scrollBy(s.vStep)  : move(s.activeIndex < 0 ? 0 : s.activeIndex + 1);
        case 'ArrowUp':    return rowless ? scrollBy(-s.vStep) : move(s.activeIndex < 0 ? 0 : s.activeIndex - 1);
        case 'PageDown':   return rowless ? scrollBy(host ? host.clientHeight * 0.9 : 0)  : move(s.activeIndex < 0 ? 0 : s.activeIndex + s.pageSize);
        case 'PageUp':     return rowless ? scrollBy(host ? -host.clientHeight * 0.9 : 0) : move(s.activeIndex < 0 ? 0 : s.activeIndex - s.pageSize);
        case 'Home':       return rowless ? scrollTo(0) : move(0);
        case 'End':        return rowless ? scrollTo(host ? host.scrollHeight : 0) : move(s.rowCount - 1);
        case 'ArrowLeft':
          e.preventDefault();
          if (s.onHorizontal?.(-1)) return;
          if (host) host.scrollLeft -= s.hStep;
          return;
        case 'ArrowRight':
          e.preventDefault();
          if (s.onHorizontal?.(1)) return;
          if (host) host.scrollLeft += s.hStep;
          return;
        case 'Enter':
          if (s.activeIndex >= 0 && s.activeIndex < s.rowCount && s.onActivate) {
            e.preventDefault();
            s.onActivate(s.activeIndex);
          }
          return;
        case 'Escape':
        case 'Backspace':
          if (s.onBack) {
            e.preventDefault();
            s.onBack();
          }
          return;
        default:
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [containerRef, scrollRowIntoView]);

  /** Move the cursor and bring it into view — for callers restoring a saved
   *  position, or driving the cursor from their own key handling. */
  const focusIndex = useCallback((index: number) => {
    setActiveIndex(index);
    requestAnimationFrame(() => scrollRowIntoView(index));
  }, [scrollRowIntoView]);

  /** Spread onto each row: marks it for scroll-into-view and highlights it. */
  const rowProps = useCallback((index: number) => ({
    'data-nav-index': index,
    'aria-selected': index === activeIndex,
  }), [activeIndex]);

  /** Highlight class for the active row — layered over the row's own colours. */
  const rowClass = useCallback(
    (index: number) => (index === activeIndex ? 'ring-2 ring-inset ring-blue-400 bg-blue-50' : ''),
    [activeIndex],
  );

  return { activeIndex, setActiveIndex, focusIndex, rowProps, rowClass };
}

export default useReportKeyboardNav;
